<template>
  <div>
    <page-header
      :actions="headerActionConfig"
      class="mt-4"
      :page="pageOptions"
    />
    <board class="mb-4" :details="boardData" />
    <validation-observer ref="insertSpaceRef" novalidate tag="form">
      <div>
        <section-title has-line title="Información de Espacio Disponible" />
        <div class="main-box col-md-12 mb-3">
          <i class="fas fa-bell-exclamation" />
          <span>
            Nota: De contar con mas de una compañía debe seleccionar una primero
            para luego seleccionar la fecha y continuar con la creación de
            espacios.
          </span>
        </div>
        <div class="row ml-2">
          <div class="col-md-4">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-date-picker
                v-model="model.startDate"
                class="w-100"
                custom-error-msg="Campo requerido."
                :disabled="disabledDate && !model.isEditMode"
                :error="errors[0]"
                label="Fecha:"
                :max-date="maxDate"
                :min-date="minStartDate"
                placeholder="DD/MM/YYYY"
              />
            </validation-provider>
            <div class="row mt-3">
              <div class="col-7">
                <div class="color-gray-6 font-14 font-weight-normal mb-2">
                  Hora Desde:
                </div>
                <div class="d-flex align-items-center">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <custom-input
                      v-model="model.startDateHour"
                      class="hour-size"
                      custom-error-msg=""
                      :disabled="disabledDate && !model.isEditMode"
                      :error="errors[0]"
                      mask-type="Day"
                      type="text"
                      @input="evalEnterHour"
                    />
                  </validation-provider>
                  <span class="mx-2 mt-1">:</span>
                  <validation-provider v-slot="{ errors }" rules="required">
                    <custom-input
                      v-model="model.startDateMinute"
                      class="hour-size"
                      custom-error-msg=""
                      :disabled="disabledDate && !model.isEditMode"
                      :error="errors[0]"
                      mask-type="Day"
                      type="text"
                      @input="evalEnterMinute"
                    />
                  </validation-provider>
                  <b-form-select
                    v-model="model.startDateAmPm"
                    class="ampm-size ml-3"
                    :disabled="
                      model.ampmDisabled || (disabledDate && !model.isEditMode)
                    "
                    :options="catalog.timeOptions"
                    @input="evalEnterAmPm"
                  />
                </div>
                <error
                  v-if="hourValidation"
                  :message="hourValidation"
                  variant="no-border"
                />
              </div>
              <div class="col-4">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-drop-down
                    v-model="model.duration"
                    :clearable="false"
                    custom-error-msg="Favor de seleccionar la duración"
                    :disabled="disabledDate && !model.isEditMode"
                    :error="errors[0]"
                    fieldkey="value"
                    fieldname="text"
                    label="Duración:"
                    :options="catalog.durations"
                    placeholder="Seleccione"
                    @input="onDurationChange"
                  />
                </validation-provider>
              </div>
            </div>

            <div class="mt-4">
              <div class="color-gray-6 font-14 font-weight-normal mb-2">
                Hora Hasta:
              </div>
              <div class="d-flex align-items-center">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-input
                    v-model="model.endDateHour"
                    class="hour-size"
                    custom-error-msg=""
                    :disabled="disabledDate && !model.isEditMode"
                    :error="errors[0]"
                    mask-type="Day"
                    type="text"
                    @input="evalEnterEndDate"
                  />
                </validation-provider>
                <span class="mx-2 mt-1">:</span>
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-input
                    v-model="model.endDateMinute"
                    class="hour-size"
                    custom-error-msg=""
                    :disabled="disabledDate"
                    :error="errors[0]"
                    mask-type="Day"
                    type="text"
                    @input="evalEnterEndDate"
                  />
                </validation-provider>
                <b-form-select
                  v-model="model.endDateAmPm"
                  class="ampm-size ml-3"
                  :disabled="
                    model.endDateAmPmDisabled ||
                      (disabledDate && !model.isEditMode)
                  "
                  :options="catalog.timeOptions"
                />
              </div>
              <error
                v-if="hourValidation"
                :message="hourValidation"
                variant="no-border"
              />
              <div v-if="isMultipleCreation" class="service-warning-container">
                <error
                  is-alert-mode
                  message="De acuerdo a la selección de horarios se estarán creando espacios disponibles en bloques."
                  variant=""
                />
              </div>
            </div>
            <div class="mt-4">
              <check-box
                v-model="model.recurrence"
                class="checkbox"
                text="Recurrencia"
                @input="onChangeRecurrence"
              />
            </div>
          </div>
          <div class="col-md-4 mt-4">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.company"
                :clearable="false"
                custom-error-msg="Campo requerido."
                :disabled="catalog.companies.length === 1 ? true : false"
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                label="Compañía:"
                :options="catalog.companies"
                placeholder="Seleccione"
                @input="onChangeCompany"
              />
            </validation-provider>

            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.municipality"
                class="mt-3"
                :clearable="false"
                custom-error-msg="Campo requerido."
                :error="errors[0]"
                fieldkey="id"
                fieldname="name"
                label="Municipio:"
                :options="catalog.municipalities"
                placeholder="Seleccione"
                @input="onChangeMunicipality"
              />
            </validation-provider>

            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.serviceLocation"
                class="mt-3"
                :clearable="false"
                custom-error-msg="Campo requerido."
                :error="errors[0]"
                fieldkey="specialistLocationId"
                fieldname="locationName"
                label="Lugar de Servicio:"
                :options="catalog.serviceLocations"
                placeholder="Seleccione"
                @input="onChangeServiceLocation"
              />
            </validation-provider>
          </div>
          <div class="col-md-4 mt-4">
            <div class="bg-secondary-9 rounded p-3 mt-2">
              <div
                class="color-secondary-6 font-weight-normal font-16 border-bottom mx-3 mb-4"
              >
                Disciplinas
              </div>
              <div
                v-for="(item, index) in catalog.serviceTypes"
                :key="`license-row-${index}`"
                class="ml-3 c-checkbox"
              >
                <check-box
                  v-model="item.value"
                  class="checkbox"
                  :text="item.name"
                />
              </div>
              <div v-if="catalog.serviceTypes.length == 0" class="text-center">
                No hay información disponible.
              </div>
            </div>
            <error
              v-if="disciplineValidation === true"
              message="Debe seleccionar al menos una disciplina."
              variant="no-border"
            />
          </div>
        </div>
      </div>
      <div v-if="model.recurrence">
        <section-title class="mt-5" has-line title="Recurrencia" />
        <recurrence-date
          class="c-recurrenceDate"
          :start-date="model.startDate"
          @input="onChangeOcurrences"
        />
      </div>
    </validation-observer>
  </div>
</template>

<script>
import moment from 'moment';
import 'moment/locale/es-do';
import { PageHeader } from '@/shared/components/PageHeader/index';
import SectionTitle from '@/components/common/page/SectionTitle';
import CustomDatePicker from 'custom-date-picker';
import { CustomDropDown, CustomInput, Error } from '@/components/common';
import RecurrenceDate from '@/components/common/recurrence/RecurrenceDate';
import {
  getAvailableSpaceMunicipalities as _getMunicipalities,
  getSpecialistServiceLocations as _getServiceLocations,
  insertAvailableSpace as _insertAvailableSpace,
  updateAvailableSpace as _updateAvailableSpace,
  canCreateAvailableSpace as _canCreateAvailableSpace,
  getSpecialistServiceTypes as _getServiceTypes,
} from '@/modules/shared/providerappointment.api';
import { providersWithContractList as _providersWithContractList } from '@/modules/shared/provider.api.js';

import alertMixin from '@/mixins/alert.mixin.js';

export default {
  name: 'SpaceManagement',
  components: {
    PageHeader,
    SectionTitle,
    CustomDatePicker,
    CustomDropDown,
    CustomInput,
    Error,
    RecurrenceDate,
  },
  mixins: [alertMixin],
  data: () => ({
    maxDate: null,
    disabledDate: true,
    fiscalYearId: null,
    specialistId: null,
    specialistInfo: null,
    disciplineValidation: null,
    hourValidation: null,
    model: {
      ampmDisabled: false,
      startDate: null,
      startDateHour: null,
      startDateMinute: null,
      startDateAmPm: null,
      endDate: null,
      endDateHour: null,
      endDateMinute: null,
      endDateAmPm: null,
      endDateAmPmDisabled: null,
      duration: null,
      company: null,
      municipality: null,
      serviceLocation: null,
      recurrence: null,
      recurrenceType: null,
    },
    catalog: {
      companies: [],
      municipalities: [],
      serviceLocations: [],
      serviceTypes: [],
      timeOptions: [
        { value: 'am', text: 'AM' },
        { value: 'pm', text: 'PM' },
      ],
      durations: [
        { value: '30', text: '30 min' },
        { value: '45', text: '45 min' },
        { value: '60', text: '60 min' },
      ],
    },
    occurrences: [],
    validations: {
      isDateValid: null,
    },
  }),
  computed: {
    pageOptions() {
      return {
        title: 'Espacios Disponibles: Crear',
        icon: 'fas fa-user-tie',
        routes: [
          'Proveedor',
          'Espacios Disponibles',
          'Espacios Disponibles',
          'Crear Espacios Disponibles',
        ],
      };
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: this.cancelHandler,
          visible: true,
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          click: this.saveHandler,
          visible: true,
        },
      ];
    },
    boardData() {
      return [
        [
          {
            label: 'Especialista:',
            icon: 'fas fa-address-book',
            data: this.specialistInfo?.specialistName,
            cols: 'col-12 col-md-4',
          },
          {
            label: 'SSN:',
            icon: 'fas fa-address-book',
            data: this.$options.filters.ssn(
              this.specialistInfo?.socialSecurityNo
            ),
            cols: 'col-12 col-md-4',
          },
          {
            label: 'Número de Especialista:',
            icon: 'fas fa-address-book',
            data: this.specialistInfo?.specialistId,
            cols: 'col-12 col-md-4',
          },
        ],
      ];
    },
    specialistSSN() {
      return (ssn) => `xxx-xx${ssn}`;
    },
    minStartDate() {
      return moment()
        .add('days', -1)
        .toDate();
    },
    isMultipleCreation() {
      let dateWithDuration = moment(
        `${this.model.startDateHour}:${this.model.startDateMinute} ${this.model.startDateAmPm}`,
        'h:m A',
        true
      ).add(parseInt(this.model.duration?.value), 'minutes');

      let endDate = moment(
        `${this.model.endDateHour}:${this.model.endDateMinute} ${this.model.endDateAmPm}`,
        'h:m A',
        true
      );

      let isValid = dateWithDuration.isValid() && endDate.isValid();

      if (isValid && endDate > dateWithDuration) {
        let duration = moment.duration(endDate.diff(dateWithDuration));
        let minutes = duration.asMinutes();

        return minutes >= this.model.duration?.value;
      }

      return false;
    },
  },
  async mounted() {
    const {
      fiscalYearId,
      specialistId,
      specialistInfo,
      editInfo,
    } = this.$route.params;
    this.fiscalYearId = fiscalYearId;
    this.specialistId = specialistId;
    this.specialistInfo = specialistInfo;

    if (editInfo) {
      await this.callGetCompanies();
      await this.callGetMunicipalities(this.model.companyId);
      await this.callGetServiceLocations(
        this.model.companyId,
        this.model.municipalityId
      );
      this.model = {
        ...this.model,
        ...editInfo,
      };

      await this.callGetServiceTypes(
        this.model.company?.id,
        this.model.municipality?.id,
        this.model.serviceLocation?.locationId
      );

      this.catalog.serviceTypes = this.catalog.serviceTypes.map((x) => {
        if (x.id === this.model.serviceId) {
          x.value = true;
        }
        return x;
      });
    } else {
      await this.callGetCompanies();
      await this.callGetMunicipalities(this.model.companyId);
      await this.callGetServiceLocations(
        this.model.companyId,
        this.model.municipalityId
      );
    }
  },
  methods: {
    async callGetCompanies() {
      const { data } = await _providersWithContractList(
        this.fiscalYearId,
        this.specialistId
      );
      this.catalog.companies = data;
      if (this.catalog.companies.length === 1) {
        this.model.company = this.catalog.companies[0];
        this.disabledDate = false;
        this.maxDate = moment(this.model.company.contractEffectiveDateTo);
        this.callGetMunicipalities(this.model.company?.id);
      }
    },
    async callGetMunicipalities(companyId) {
      const { data } = await _getMunicipalities(companyId, this.specialistId);
      this.catalog.municipalities = data;
    },
    async callGetServiceLocations(companyId, municipalityId) {
      const { data } = await _getServiceLocations(
        companyId,
        municipalityId,
        this.specialistId
      );
      this.catalog.serviceLocations = data;
    },
    async callGetServiceTypes(companyId, municipalityId, locationId) {
      const { data } = await _getServiceTypes(
        companyId,
        this.specialistId,
        municipalityId,
        locationId
      );
      this.catalog.serviceTypes = data;
    },
    onChangeCompany(value) {
      if (value) {
        this.disabledDate = false;
        this.catalog.municipalities = [];
        this.catalog.serviceLocations = [];
        this.catalog.serviceTypes = [];
        this.model.municipality = null;
        this.model.serviceLocation = null;
        this.maxDate = moment(value.contractEffectiveDateTo);

        this.callGetMunicipalities(value?.id);
      }
    },
    onChangeMunicipality(value) {
      if (value) {
        this.model.serviceLocation = null;
        this.callGetServiceLocations(this.model.company?.id, value?.id);
      }
    },
    onChangeServiceLocation(value) {
      if (value) {
        this.callGetServiceTypes(
          this.model.company?.id,
          this.model.municipality?.id,
          this.model.serviceLocation?.locationId
        );
      }
    },
    onChangeOcurrences({ ocurrences, isValid }) {
      this.occurrences = ocurrences;
      this.validations.isDateValid = isValid;
    },
    onChangeRecurrence(value) {
      if (!value) {
        this.validations.isDateValid = null;
      }
    },
    cancelHandler() {
      this.$router.go(-1);
    },
    async clearModel() {
      this.model = {
        ...this.model,
        startDate: null,
        startDateHour: null,
        startDateMinute: null,
        startDateAmPm: null,
        endDate: null,
        endDateHour: null,
        endDateMinute: null,
        endDateAmPm: null,
        duration: null,
        municipality: null,
        company:
          this.catalog.companies.length === 1 ? this.model.company : null,
        serviceLocation: null,
        recurrence: null,
      };
      this.catalog = {
        ...this.catalog,
        serviceLocations: [],
        serviceTypes: [],
      };
      await this.$refs.insertSpaceRef.reset();
    },
    async saveHandler() {
      const model = this.model;
      //validated empty fields
      const isValid = await this.$refs.insertSpaceRef.validate();

      //validate disciplines
      const _hasSelectedDiscipline = this.catalog.serviceTypes.filter(
        (x) => x.value == true
      ).length;
      this.disciplineValidation = _hasSelectedDiscipline === 0 ? true : null;

      //validate hour
      const _hasValidHour = this.isHourValid();
      this.hourValidation = !_hasValidHour ? 'La hora no es válida' : null;

      //validate over limit
      let _hourEndOverLimit = null;
      if (_hasValidHour == true) {
        _hourEndOverLimit = this.isHourEndOverLimit(model.endDateHour);
        this.hourValidation = _hourEndOverLimit
          ? 'La hora sobrepasa el limite permitido (6:00am a 7:00pm).'
          : null;
      }

      const _isDateValid = model.recurrence && !this.validations.isDateValid;

      if (
        !isValid ||
        _hasSelectedDiscipline === 0 ||
        !_hasValidHour ||
        _hourEndOverLimit ||
        _isDateValid
      ) {
        this.ShowToast('Alerta', 'Información Incompleta', 'error');
        return;
      }

      const paramsToSend = {
        clientAppointmentSpaceID: model.clientAppointmentSpaceID ?? null,
        startDate: this.getStartHour(
          model.startDate,
          parseInt(model.startDateHour),
          parseInt(model.startDateMinute)
        )
          .format()
          .slice(0, -6),
        endDate: this.getDateObj(
          model.startDate,
          model.endDateHour,
          model.endDateMinute,
          model.endDateAmPm
        )
          .format()
          .slice(0, -6),
        duration: parseInt(this.model.duration?.value) || 0,
        serviceIds: this.catalog.serviceTypes
          .filter((x) => x.value == true)
          .map((x) => x.id),
        locationServiceId: parseInt(
          model.serviceLocation?.specialistLocationId
        ),
        hasOcurrence: model.recurrence === true,
        occurrences:
          this.occurrences?.length > 0
            ? this.getMapOcurrences(this.occurrences)
            : [],
      };

      if (model.isEditMode) {
        _updateAvailableSpace(paramsToSend)
          .then(() => {
            this.ShowToast(
              'Proceso Exitoso',
              'El espacio ha sido guardado exitosamente.',
              'success'
            );
          })
          .catch((error) => {
            this.ShowToast('', error.response.data.message, 'error');
          });
      } else {
        await _canCreateAvailableSpace()
          .then(() => {
            _insertAvailableSpace(paramsToSend)
              .then(() => {
                this.ShowToast(
                  'Proceso Exitoso',
                  'El espacio ha sido creado exitosamente.',
                  'success'
                );
                this.clearModel();
              })
              .catch((error) => {
                this.ShowToast('', error.response.data.message, 'error');
              });
          })
          .catch((error) => {
            this.ShowToast('', error.response.data.message, 'error');
          });
      }
    },
    getMapOcurrences(array) {
      return array.map((x) => {
        return {
          startTime: this.getStartHour(
            x,
            parseInt(this.model.startDateHour),
            parseInt(this.model.startDateMinute)
          )
            .format()
            .slice(0, -6),
          endTime: this.getDateObj(
            x,
            parseInt(this.model.endDateHour),
            parseInt(this.model.endDateMinute),
            this.model.endDateAmPm
          )
            .format()
            .slice(0, -6),
        };
      });
    },
    evalEnterHour() {
      if (this.model.startDateHour) {
        const valueHour = parseInt(this.model.startDateHour);
        const ampmResult = this.getAmPm(valueHour);

        this.model.ampmDisabled = ampmResult.disabled;
        this.model.startDateAmPm = ampmResult.time;

        this.evalEndHour();
      }
    },
    evalEnterMinute() {
      this.evalEndHour();
    },
    evalEnterAmPm() {
      this.evalEndHour();
    },
    evalEnterEndDate() {
      if (this.model.endDateHour)
        this.model.endDateHour = parseInt(this.model.endDateHour);
      if (this.model.endDateMinute)
        this.model.endDateMinute = parseInt(this.model.endDateMinute);

      let resultAmPm = this.getAmPm(this.model.endDateHour);
      this.model.endDateAmPm = resultAmPm.time;
      this.model.endDateAmPmDisabled = resultAmPm.disabled;
    },
    evalEndHour() {
      if (
        this.model.startDateHour &&
        this.model.startDateMinute &&
        this.model.startDateAmPm
      ) {
        const isHourValid = this.isHourValid();
        if (isHourValid) {
          this.setEndHour();
        } else {
          this.model.endDateHour = '';
        }
      }
    },
    isHourValid() {
      if (
        this.model.startDateHour &&
        this.model.startDateMinute &&
        this.model.startDateAmPm
      ) {
        const hour = parseInt(this.model.startDateHour, 10);
        const minute = this.model.startDateMinute;
        const ampm = this.model.startDateAmPm;

        const _isValid = moment(
          `${hour}:${minute} ${ampm}`,
          'h:mm A',
          true
        ).isValid();

        return _isValid;
      }
    },
    isHourEndOverLimit(hour) {
      const from = moment(hour, 'hh:mm A');
      const to = moment('7:00 pm', 'hh:mm A');
      return from.isAfter(to);
    },
    getDateObj(date, hour, minute, amPm) {
      const _hour = this.eval24Hour(amPm, parseInt(hour));
      return moment(date).set({
        hour: _hour,
        minute: minute,
        second: 0,
      });
    },
    getStartHour(date, hour, minute) {
      return this.getDateObj(
        date,
        this.model.startDateHour,
        minute,
        this.model.startDateAmPm
      );
    },
    onDurationChange() {
      this.setEndHour();
    },
    setEndHour() {
      if (!this.isHourValid()) {
        this.model.endDateHour = '';
        return;
      }

      const hour = this.eval24Hour(
        this.model.startDateAmPm,
        parseInt(this.model.startDateHour)
      );
      const minute = parseInt(this.model.startDateMinute) || 0;
      const endDate = moment(new Date()).set({
        hour: hour,
        minute: minute + parseInt(this.model.duration.value),
      });
      const amOrPmText = endDate.format('h:mm a').split(' ')[1];

      this.model.endDateHour = this.get12HoursFormat(
        amOrPmText,
        endDate.hour()
      );
      this.model.endDateMinute = endDate.minute();
      this.model.endDateAmPm = amOrPmText;
    },
    getEndHour(date, hour, minute, formated = true) {
      const _hour = this.eval24Hour(
        this.model.startDateAmPm,
        parseInt(this.model.startDateHour)
      );
      const endDate = moment(date).set({
        hour: _hour,
        minute: minute + parseInt(this.model.duration.value),
      });

      if (formated) {
        return endDate.format('h:mm a');
      }
      return endDate.toDate();
    },
    getAmPm(valueHour) {
      let result = { time: 'am', disabled: true };
      if (valueHour == 12 || (valueHour >= 1 && valueHour <= 5)) {
        result.time = 'pm';
      } else if (valueHour >= 8 && valueHour <= 11) {
        result.time = 'am';
      } else {
        result.disabled = false;
      }
      return result;
    },
    eval24Hour(ampm, hour) {
      let output = hour;
      if (ampm == 'pm') {
        if (hour != 12) {
          output = hour + 12;
        }
      } else if (ampm == 'am') {
        if (hour == 12) {
          output = 0;
        }
      }
      return output;
    },
    get12HoursFormat(ampm, hour) {
      let output = hour;
      if (ampm == 'pm' && hour > 12) output = hour - 12;

      return output;
    },
  },
};
</script>

<style scoped lang="scss">
.main-box {
  display: flex;
  background: #fff5e8 0% 0% no-repeat padding-box;
  border: 1px solid #fdac41;
  border-radius: 4px;
  opacity: 1;
  padding: 10px;
  margin-top: 10px;
  margin-left: 14px;
}

.main-box i {
  color: #af6708;
  opacity: 1;
  margin-right: 10px;
  margin-top: 5px;
}

.main-box span {
  text-align: left;
  font: Regular 14px/24px Lato;
  letter-spacing: 0;
  color: #af6708;
  opacity: 1;
}
.hour-size {
  width: 60px;
  margin-right: 10px;
}
.ampm-size {
  width: 80px;
  margin-right: 10px;
}

::v-deep .input-group.checkbox label span + div {
  font-size: 16px;
  color: #11291b;
}

.c-checkbox {
  margin-top: 12px;
  margin-bottom: 12px;
}

@media only screen and (max-width: 600px) {
  ::v-deep .custom-datetime-picker-container {
    width: 100% !important;
    input[type='text'] {
      width: 100%;
    }
  }
}

::v-deep .custom-control {
  border-bottom: 1px solid #e2e2e2;
  padding: 10px 0 10px 40px;
}

@media only screen and (max-width: 768px) {
  .c-recurrenceDate {
    ::v-deep .row.ml-2 {
      margin-top: 20px;
      margin-left: 100px !important;

      .d-flex.border.rounded.mt-3.ml-n5.pr-3 {
        margin-left: 0 !important;
        flex-direction: column;
        padding: 10px;
      }

      .col-md-3 {
        padding-left: 0 !important;
      }
    }

    ::v-deep .col-md-12.d-flex.align-items-center.mt-4 {
      flex-direction: column;
      align-items: flex-start !important;
      margin-left: 40px;

      .color-secondary-6.font-16.font-weight-normal.ml-1 {
        width: 60px !important;
      }

      .custom-ddl {
        width: 100px !important;
      }

      .d-flex.align-items-center.ml-4 {
        margin-top: 10px !important;
        margin-left: 0 !important;
      }
    }
  }
}
</style>
